import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Anchor,
  MapPin,
  ScrollText,
  ShieldCheck,
  Layers,
  Handshake,
  Target,
  Eye,
  ClipboardCheck,
} from "lucide-react";
import Reveal from "@/components/site/Reveal";
import Icon from "@/components/site/Icon";
import AsiaNetwork from "@/components/site/AsiaNetwork";
import ProcessTimeline from "@/components/site/ProcessTimeline";
import ServiceDecor from "@/components/site/ServiceDecor";
import PageSeo from "@/components/site/PageSeo";
import CountryCoverage from "@/components/services/CountryCoverage";
import { HOME } from "@/constants/testIds";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";
import { useServices } from "@/hooks/useServices";
import { trackCtaClick } from "@/lib/ga";

// Icons whitelist for CMS-selected block icons.
const ICONS = { Anchor, MapPin, ScrollText, ShieldCheck, Layers, Handshake, Target, Eye, ClipboardCheck };

export default function Home() {
  const { data: wc } = useWebsiteContent();
  const { services } = useServices();

  const featuredSlugs = (wc?.homeServicesFeaturedSlugs || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const featured = featuredSlugs
    .map((slug) => services.find((s) => s.slug === slug))
    .filter(Boolean);

  const whyBlocks     = wc?.homeWhyBlocks || [];
  const processSteps  = (wc?.processSteps || []).map((s) => ({ n: s.number, title: s.title, body: s.body }));
  const governance    = wc?.homeGovernanceItems || [];

  return (
    <main>
      <PageSeo
        path="/"
        title="Your execution partner in Asia"
        description="MTS is your accountable B2B execution partner in Asia — sourcing, factory verification, production management, quality control and international trading."
        type="website"
      />
      {/* Organization + WebSite JSON-LD are emitted globally by
          <SiteSchemas /> in App.js — sourced from CMS SiteSettings so
          business info (sameAs, contactPoint, address, logo) stays
          editorial. Home no longer needs a local Organization block. */}
      {/* HERO ============================================================= */}
      <section
        data-testid={HOME.hero}
        className="relative overflow-hidden border-b border-[#ececef] mts-soft-gradient"
      >
        {/* faint background dot grid — ambient across the whole hero */}
        <div
          aria-hidden
          className="absolute inset-0 mts-dot-grid opacity-40 [mask-image:radial-gradient(65%_65%_at_50%_40%,black,transparent)]"
        />

        {/* Ambient animation — spans the full hero height so the world map
            is visible in every free area around the copy (including the
            top-right, right of "in Asia"). Opacity kept low so the text
            remains fully readable. */}
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 w-full lg:w-[74%] pointer-events-none opacity-35 md:opacity-45 lg:opacity-55"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 30%, black 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, black 30%, black 100%)",
          }}
        >
          <div className="relative h-full w-full mts-float flex items-center justify-end">
            <AsiaNetwork className="w-full h-auto max-h-[560px]" />
          </div>
        </div>

        {/* Foreground: copy + CTA sit naturally under the sticky header. */}
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-20 md:pb-28">
          <div className="max-w-[720px]">
            <Reveal>
              <h1 className="text-[40px] leading-[1.02] sm:text-[56px] md:text-[72px] lg:text-[80px] font-semibold tracking-[-0.03em] text-[#0a0a0a] text-balance">
                {wc?.homeHeroTitlePrefix || ""}
                <br className="hidden sm:block" />{" "}
                <span
                  className="italic font-normal"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {wc?.homeHeroTitleItalic || ""}
                </span>{" "}
                {wc?.homeHeroTitleSuffix || ""}
              </h1>
            </Reveal>

            <Reveal delay={140}>
              <div className="mt-7 md:mt-9 flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
                <p className="text-[16px] md:text-[18px] leading-[1.55] text-[#424245] max-w-lg text-pretty">
                  {wc?.homeHeroSubtitle || ""}
                </p>
                <Link
                  to="/contact"
                  data-testid={HOME.heroCtaStart}
                  onClick={() => trackCtaClick(wc?.homeHeroCtaLabel || "Start a Project", "home-hero")}
                  className="mts-btn-primary shrink-0 self-start md:self-end"
                >
                  {wc?.homeHeroCtaLabel || "Start a Project"} <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US =================================================== */}
      <section
        data-testid={HOME.whyChoose}
        className="border-b border-[#ececef] bg-[#f7f7f8]"
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                {wc?.homeWhyEyebrow || ""}
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 text-[36px] sm:text-[44px] md:text-[56px] font-semibold tracking-[-0.03em] text-[#0a0a0a] leading-[1.05] text-balance">
                {wc?.homeWhyTitlePrefix || ""}{" "}
                <span
                  className="italic font-normal"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {wc?.homeWhyTitleItalic || ""}
                </span>
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-6 text-[16px] leading-relaxed text-[#424245] max-w-xl mx-auto">
                {wc?.homeWhyIntro || ""}
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyBlocks.map((b, i) => {
              const IconCmp = ICONS[b.icon] || Layers;
              return (
                <Reveal key={b.title || i} delay={i * 40}>
                  <div className="mts-card p-6 md:p-7 h-full">
                    <div className="flex items-center gap-4">
                      <div className="mts-icon-chip shrink-0">
                        <IconCmp size={20} strokeWidth={1.5} />
                      </div>
                      <h3 className="text-[17px] md:text-[18px] font-medium text-[#0a0a0a] tracking-tight leading-snug">
                        {b.title}
                      </h3>
                    </div>
                    <p className="mt-4 text-[14px] leading-relaxed text-[#6e6e73]">
                      {b.description}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW =============================================== */}
      <section
        data-testid={HOME.servicesOverview}
        className="border-b border-[#ececef] bg-white"
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                {wc?.homeServicesEyebrow || ""}
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 text-[36px] sm:text-[44px] md:text-[56px] font-semibold tracking-[-0.03em] text-[#0a0a0a] leading-[1.05] text-balance">
                {wc?.homeServicesTitlePrefix || ""}{" "}
                <span
                  className="italic font-normal"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {wc?.homeServicesTitleItalic || ""}
                </span>
              </h2>
            </Reveal>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((s, i) => (
              <Reveal key={s.slug} delay={i * 40}>
                <Link
                  to={`/services#${s.slug}`}
                  data-testid={HOME.serviceCard(s.slug)}
                  className="mts-card relative overflow-hidden block p-6 md:p-7 h-full group"
                >
                  {/* Decorative corner motif — unique per service (matches Services page) */}
                  <div className="absolute -top-3 -right-3 pointer-events-none opacity-70">
                    <ServiceDecor slug={s.slug} />
                  </div>
                  <div className="relative flex items-center gap-4">
                    <div className="mts-icon-chip shrink-0">
                      <Icon name={s.icon} size={20} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-[18px] md:text-[20px] font-medium tracking-tight text-[#0a0a0a] leading-snug">
                      {s.title}
                    </h3>
                  </div>
                  <p className="relative mt-4 text-[14px] leading-relaxed text-[#6e6e73]">
                    {s.summary}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTRY COVERAGE ================================================ */}
      <CountryCoverage variant="compact" source="home" />

      {/* HOW WE WORK ===================================================== */}
      <section
        data-testid={HOME.howWeWork}
        className="border-b border-[#ececef] bg-[#f7f7f8] relative"
      >
        <div className="absolute inset-0 mts-fine-grid opacity-30 pointer-events-none" aria-hidden />
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
                {wc?.homeHowEyebrow || ""}
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 text-[36px] sm:text-[44px] md:text-[56px] font-semibold tracking-[-0.03em] text-[#0a0a0a] leading-[1.05] text-balance">
                {wc?.homeHowTitlePrefix || ""}{" "}
                <span
                  className="italic font-normal"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {wc?.homeHowTitleItalic || ""}
                </span>
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-6 text-[15px] leading-relaxed text-[#424245] max-w-xl mx-auto">
                {wc?.homeHowIntro || ""}
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <Reveal delay={80}>
                <div className="rounded-2xl bg-white border border-[#ececef] p-6">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                    {wc?.homeGovernanceTitle || ""}
                  </p>
                  <ul className="mt-4 space-y-3 text-[13px] text-[#0a0a0a]">
                    {governance.map((g, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="h-1 w-1 rounded-full bg-[#0a0a0a]" />
                        {g.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>

            <div className="md:col-span-7">
              <ProcessTimeline steps={processSteps} />
            </div>
          </div>
        </div>
      </section>

      {/* AI SUPPORT ENTRY ================================================ */}
      <section
        data-testid={HOME.aiSupportEntry}
        className="border-b border-[#ececef] bg-[#f7f7f8] relative overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute -right-24 -top-24 h-[360px] w-[360px] rounded-full bg-white/70 blur-3xl"
        />
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <Reveal>
              <h2 className="text-[36px] sm:text-[44px] md:text-[56px] font-semibold tracking-[-0.03em] text-[#0a0a0a] leading-[1.05] text-balance">
                {wc?.homeSupportTitle || ""}
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-[15px] leading-relaxed text-[#424245] max-w-xl mx-auto">
                {wc?.homeSupportIntro || ""}
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/support"
                  className="inline-flex items-center rounded-full border border-[#d2d2d7] bg-white px-5 py-2.5 text-[14px] font-medium text-[#0a0a0a] hover:border-[#0a0a0a] transition-colors"
                >
                  {wc?.homeSupportKbLabel || "Knowledge Base"}
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-full border border-[#d2d2d7] bg-white px-5 py-2.5 text-[14px] font-medium text-[#0a0a0a] hover:border-[#0a0a0a] transition-colors"
                  onClick={() => trackCtaClick(wc?.homeSupportContactLabel || "Contact us", "home-support-band")}
                >
                  {wc?.homeSupportContactLabel || "Contact us"}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA — dark panel ========================================= */}
      <section data-testid={HOME.finalCta} className="relative overflow-hidden">
        <div className="mts-dark-panel">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28 relative">
            {/* subtle decorative arc */}
            <svg
              aria-hidden
              className="absolute -top-20 -right-24 opacity-30"
              width="520"
              height="520"
              viewBox="0 0 520 520"
              fill="none"
            >
              <circle cx="260" cy="260" r="200" stroke="#ffffff" strokeOpacity="0.12" />
              <circle cx="260" cy="260" r="140" stroke="#ffffff" strokeOpacity="0.08" strokeDasharray="2 6" />
              <circle cx="260" cy="260" r="80" stroke="#ffffff" strokeOpacity="0.06" />
            </svg>

            <Reveal>
              <h2 className="text-[44px] sm:text-[64px] md:text-[80px] font-semibold tracking-[-0.03em] text-white leading-[1.02] max-w-4xl text-balance">
                {wc?.homeFinalCtaTitlePrefix || ""}{" "}
                <span
                  className="italic font-normal text-white/90"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {wc?.homeFinalCtaTitleItalic || ""}
                </span>
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-6 text-[16px] md:text-[18px] leading-relaxed text-white/70 max-w-xl">
                {wc?.homeFinalCtaBody || ""}
              </p>
            </Reveal>

            <Reveal delay={220}>
              <div className="mt-10">
                <Link
                  to="/contact"
                  data-testid={HOME.finalCtaButton}
                  onClick={() => trackCtaClick(wc?.homeFinalCtaLabel || "Start a Project", "home-final-cta")}
                  className="mts-btn-inverse"
                >
                  {wc?.homeFinalCtaLabel || "Start a Project"} <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
